// src/components/farm/FarmCard.jsx
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import StoreCardStyle from "../../assets/stylesheets/store/StoreCard.module.css";
import SkeletonCard from "../common/SkeletonCard";

import Logger from "../../utils/logger";

const log = Logger.create("StoreCard");

export default function StoreCard({
  image,
  name,
  description,
  price,
  unit,
  currency,
  ctaLabel,
  onBuy,
  className,
  ...rest
}) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Preload image to control skeleton state
  useEffect(() => {
    setImageLoaded(false);
    if (!image) {
      setImageLoaded(true);
      return;
    }
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => {
      log.warn("Image failed to load", { image });
      setImageLoaded(true);
    };
    img.src = image;
  }, [image]);

  const isNumericPrice = typeof price === "number" && Number.isFinite(price);
  const formattedPrice = isNumericPrice ? price.toFixed(2) : String(price ?? "");

  const handleBuy = (e) => {
    e?.preventDefault?.();
    onBuy?.({ image, name, description, price, unit });
  };

  // Loading state
  if (!imageLoaded) {
    return <SkeletonCard className={className} />;
  }

  // After loading
  return (
    <div className={`${StoreCardStyle['card']} ${className || ""}`} {...rest}>
      <div className={StoreCardStyle['media']}>
        {image ? (
          <img
            className={StoreCardStyle['image']}
            src={image}
            alt={name || "Product image"}
            loading="lazy"
          />
        ) : null}
      </div>

      <div className={StoreCardStyle['body']}>
        {name ? <h3 className={StoreCardStyle['title']}>{name}</h3> : null}
        {description ? (
          <p className={StoreCardStyle['description']}>{description}</p>
        ) : null}
      </div>

      <div className={StoreCardStyle['footer']}>
        <div className={StoreCardStyle['price']}>
          {isNumericPrice ? currency : null}
          {formattedPrice}
          {isNumericPrice && unit ? (
            <span className={StoreCardStyle['unit']}>/{unit}</span>
          ) : null}
        </div>
        <button
          type="button"
          className={StoreCardStyle['buyButton']}
          onClick={handleBuy}
          aria-label={`${ctaLabel} ${name || "item"}`}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

StoreCard.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  unit: PropTypes.string,
  currency: PropTypes.string,
  ctaLabel: PropTypes.string,
  onBuy: PropTypes.func,
  className: PropTypes.string,
};

StoreCard.defaultProps = {
  image: undefined,
  name: "",
  description: "",
  unit: "lb",
  currency: "$",
  ctaLabel: "Buy",
  onBuy: undefined,
  className: "",
};

